/**
 * MpTable.tsx —— 多人对战牌桌（对局进行中）。
 *
 * 胜率门控红线：本组件在对局进行中**不渲染任何赢率入口/数值**；只有当本手结束
 * （phase = handComplete / gameOver）后，才出现「复盘赢率」入口，点击打开 MpReview 抽屉。
 */

import { useEffect, useRef, useState } from 'react';
import { mpClient, type MpState } from '../../net/mpClient';
import type { GameView, Action, LegalActions } from '../../net/protocol';
import { chips } from '../../state/format';
import { PlayingCard } from '../primitives';
import { MpReview } from './MpReview';
import { MpHistoryDrawer, MpSettingsPanel } from './MpMenus';
import { MpAvatar, CrownIcon, MpLeaveConfirm, PresenceBadge, tableSeatStyle } from './mpVisuals';

export function MpTable({ state, onLeave }: { state: MpState; onLeave: () => void }) {
  const [menu, setMenu] = useState<'none' | 'history' | 'settings'>('none');
  const [confirmLeave, setConfirmLeave] = useState(false);
  const v = state.view;
  if (!v) return <div className="mp-loading">正在进入牌桌…</div>;

  const handOver = v.phase === 'handComplete' || v.phase === 'gameOver';
  const gameOver = v.phase === 'gameOver';
  const youAreHost = !!v.seats.find((s) => s.seat === v.you.seat && s.isHost);
  // 本手进行中离开会被判弃牌 → 需二次确认（BUG-R15-02 / D-S6）
  const inHand = !handOver && v.phase !== 'idle';

  const requestLeave = () => { if (inHand) setConfirmLeave(true); else onLeave(); };

  return (
    <div className="mp-table-root">
      <header className="mp-topbar">
        <button className="mp-btn mp-btn--ghost mp-btn--tiny" onClick={requestLeave}>← 离开</button>
        <span className="mp-topbar__code">房间 {state.code}</span>
        <span className="mp-topbar__hand">第 {v.handNumber} 手</span>
        <button className="mp-btn mp-btn--tiny" onClick={() => setMenu('history')}>🗂 手牌历史</button>
        <button className="mp-btn mp-btn--tiny" onClick={() => setMenu('settings')}>⚙ 设置</button>
        {/* 门控：对局进行中赢率入口置灰不可用，且不展示任何赢率数值 */}
        {handOver && !gameOver ? (
          <button className="mp-btn mp-btn--tiny mp-btn--accent" onClick={() => mpClient.openReview()}>
            📈 复盘赢率
          </button>
        ) : (
          <button
            className="mp-btn mp-btn--tiny mp-btn--locked"
            disabled
            aria-disabled="true"
            title="多人对局中实时赢率已锁定，本手结束后可在复盘查看"
          >
            🔒 赢率
          </button>
        )}
      </header>

      <Felt view={v} />

      {handOver
        ? <HandEndBar view={v} youAreHost={youAreHost} onReview={() => mpClient.openReview()} />
        : <ActionControls view={v} />}

      {state.reviewOpen && state.review && (
        <MpReview review={state.review} onClose={() => mpClient.closeReview()} />
      )}

      {menu === 'history' && <MpHistoryDrawer state={state} onClose={() => setMenu('none')} />}
      {menu === 'settings' && <MpSettingsPanel onClose={() => setMenu('none')} />}
      {confirmLeave && (
        <MpLeaveConfirm onCancel={() => setConfirmLeave(false)} onConfirm={() => { setConfirmLeave(false); onLeave(); }} />
      )}
    </div>
  );
}

/* ------------------------- 牌桌区 ------------------------- */

function Felt({ view }: { view: GameView }) {
  const me = view.seats.find((s) => s.seat === view.you.seat) ?? null;
  const total = view.seats.length;
  /*
   * D-R16-1 / D-R16-2：牌桌席位沿桌面**环形**分布，「你」固定在底部，
   * 其余按座位号顺时针铺开（slot = (seat - yourSeat + n) % n）。
   * 宽屏用绝对定位环形，窄屏（<720px）由 CSS 回落为原「对手横排 + 我在底部」的紧凑布局。
   */
  const slotOf = (seat: number): number => (total > 0 ? ((seat - view.you.seat) + total * 2) % total : 0);
  const others = view.seats
    .filter((s) => s.seat !== view.you.seat)
    .sort((a, b) => slotOf(a.seat) - slotOf(b.seat));

  return (
    <div className="mp-felt" data-seats={total}>
      <div className="mp-opponents" data-n={others.length}>
        {others.map((s) => (
          <div key={s.seat} className="mp-ringslot" style={tableSeatStyle(slotOf(s.seat), total)}>
            <SeatBox seat={s} view={view} />
          </div>
        ))}
      </div>

      <div className="mp-center">
        <div className="mp-board">
          {view.board.map((c, i) => <PlayingCard key={i} card={c} size="md" />)}
          {Array.from({ length: Math.max(0, 5 - view.board.length) }).map((_, i) => (
            <span key={`ph${i}`} className="mp-board__placeholder" />
          ))}
        </div>
        <div className="mp-pot">底池 <strong>{chips(view.pot)}</strong></div>
      </div>

      {me && (
        <div className="mp-ringslot mp-ringslot--me" style={tableSeatStyle(0, total)}>
          <SeatBox seat={me} view={view} isMe />
        </div>
      )}
    </div>
  );
}

function SeatBox({ seat, view, isMe = false }: { seat: GameView['seats'][number]; view: GameView; isMe?: boolean }) {
  const isTurn = view.toAct === seat.seat;
  const isButton = view.buttonSeat === seat.seat;
  const cls = [
    'mp-seatbox',
    isMe ? 'mp-seatbox--me' : '',
    isTurn ? 'mp-seatbox--turn' : '',
    seat.folded ? 'mp-seatbox--folded' : '',
    seat.eliminated ? 'mp-seatbox--out' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={cls}>
      <div className="mp-seatbox__head">
        <MpAvatar name={seat.name} seat={seat.seat} size={isMe ? 32 : 24} />
        <span className="mp-seatbox__name">
          {seat.name}{isMe && ' (你)'}
          {seat.isHost && <CrownIcon />}
          {isButton && <span className="mp-dealer" title="按钮位">D</span>}
        </span>
        <PresenceBadge connected={seat.connected} presence={seat.presence} />
      </div>
      <div className="mp-seatbox__cards">
        {seat.hole
          ? seat.hole.map((c, i) => <PlayingCard key={i} card={c} size={isMe ? 'lg' : 'sm'} />)
          : seat.hasCards
            ? <><span className="mp-cardback" /><span className="mp-cardback" /></>
            : <span className="mp-seatbox__nocards">—</span>}
      </div>
      <div className="mp-seatbox__foot">
        <span className="mp-seatbox__stack">{chips(seat.stack)}</span>
        {seat.streetContributed > 0 && <span className="mp-seatbox__bet">投 {chips(seat.streetContributed)}</span>}
        {seat.folded && <span className="mp-seatbox__tag">已弃牌</span>}
        {seat.allIn && <span className="mp-seatbox__tag mp-seatbox__tag--allin">全下</span>}
        {isTurn && <TurnClock view={view} />}
      </div>
    </div>
  );
}

function TurnClock({ view }: { view: GameView }) {
  const [, force] = useState(0);
  const deadlineRef = useRef<number>(0);
  useEffect(() => {
    if (view.clockRemainingMs == null) { deadlineRef.current = 0; return; }
    deadlineRef.current = Date.now() + view.clockRemainingMs;
    const id = setInterval(() => force((x) => x + 1), 300);
    return () => clearInterval(id);
  }, [view.version, view.clockRemainingMs]);
  if (view.clockRemainingMs == null || deadlineRef.current === 0) return null;
  const remain = Math.max(0, deadlineRef.current - Date.now());
  const pct = view.clockTotalMs > 0 ? Math.max(0, Math.min(1, remain / view.clockTotalMs)) : 0;
  return (
    <span className="mp-clock" aria-label="行动倒计时">
      <span className="mp-clock__bar" style={{ width: `${pct * 100}%` }} />
      <span className="mp-clock__txt">{Math.ceil(remain / 1000)}s</span>
    </span>
  );
}

/* ------------------------- 行动控件 ------------------------- */

function ActionControls({ view }: { view: GameView }) {
  const legal = view.you.legal;
  const yourTurn = view.you.isYourTurn && !!legal;
  const me = view.seats.find((s) => s.seat === view.you.seat);
  const myStack = me?.stack ?? 0;

  if (!yourTurn || !legal) {
    return (
      <div className="mp-actions mp-actions--wait">
        <span className="mp-actions__wait">
          {view.toAct != null ? `等待 ${view.seats.find((s) => s.seat === view.toAct)?.name ?? '对手'} 行动…` : '等待发牌…'}
        </span>
      </div>
    );
  }
  return <ActiveActions legal={legal} myStack={myStack} bigBlind={Math.max(1, view.currentBet || 1)} />;
}

function ActiveActions({ legal, myStack }: { legal: LegalActions; myStack: number; bigBlind: number }) {
  const range = legal.raise ?? legal.bet;
  const [amount, setAmount] = useState<number>(range ? range.min : 0);
  const [sizerOpen, setSizerOpen] = useState(false);

  // 每次轮到行动，滑杆归位到最小额
  useEffect(() => { if (range) setAmount(range.min); }, [range?.min, range?.max]);

  const submit = (a: Action) => { mpClient.act(a); setSizerOpen(false); };

  const raiseLabel = legal.raise ? '加注' : '下注';

  return (
    <div className="mp-actions">
      {sizerOpen && range && (
        <div className="mp-sizer">
          <div className="mp-sizer__row">
            <input
              type="range" min={range.min} max={range.max} value={amount}
              onChange={(e) => setAmount(Number(e.target.value))} className="mp-sizer__slider"
            />
            <input
              type="number" min={range.min} max={range.max} value={amount} className="mp-input mp-sizer__num"
              onChange={(e) => setAmount(Math.max(range.min, Math.min(range.max, Number(e.target.value) || range.min)))}
            />
          </div>
          <div className="mp-sizer__quick">
            <button className="mp-btn mp-btn--tiny" onClick={() => setAmount(range.min)}>最小</button>
            <button className="mp-btn mp-btn--tiny" onClick={() => setAmount(Math.round((range.min + range.max) / 2))}>中间</button>
            <button className="mp-btn mp-btn--tiny" onClick={() => setAmount(range.max)}>最大</button>
            <button className="mp-btn mp-btn--primary mp-btn--tiny" onClick={() => submit({ type: legal.raise ? 'raise' : 'bet', amount })}>
              确认{raiseLabel}到 {chips(amount)}
            </button>
          </div>
        </div>
      )}
      <div className="mp-actions__row">
        {legal.fold && <button className="mp-btn mp-btn--fold" onClick={() => submit({ type: 'fold' })}>弃牌</button>}
        {legal.check && <button className="mp-btn mp-btn--check" onClick={() => submit({ type: 'check' })}>过牌</button>}
        {legal.call && (
          <button className="mp-btn mp-btn--call" onClick={() => submit({ type: 'call' })}>
            跟注 {chips(legal.call.amount)}{legal.call.allIn ? '(全下)' : ''}
          </button>
        )}
        {range && (
          <button className="mp-btn mp-btn--raise" onClick={() => setSizerOpen((o) => !o)}>
            {raiseLabel}…
          </button>
        )}
        {legal.allIn && !range && (
          <button className="mp-btn mp-btn--raise" onClick={() => submit({ type: 'allin' })}>
            全下 {chips(legal.allIn.chips)}
          </button>
        )}
      </div>
      <div className="mp-actions__meta">我的筹码 {chips(myStack)}</div>
    </div>
  );
}

/* ------------------------- 手牌结束条 ------------------------- */

function HandEndBar({ view, youAreHost, onReview }: { view: GameView; youAreHost: boolean; onReview: () => void }) {
  const gameOver = view.phase === 'gameOver';
  return (
    <div className="mp-handend">
      {gameOver ? (
        <span className="mp-handend__title">🏆 本场对局结束</span>
      ) : (
        <span className="mp-handend__title">本手结束</span>
      )}
      <div className="mp-handend__actions">
        <button className="mp-btn mp-btn--accent" onClick={onReview}>📈 复盘赢率</button>
        {!gameOver && youAreHost && (
          <button className="mp-btn mp-btn--primary" onClick={() => mpClient.nextHand()}>下一手</button>
        )}
        {!gameOver && !youAreHost && <span className="mp-hint">等待房主开始下一手…</span>}
      </div>
    </div>
  );
}
